/// <amd-module name="Wit/WorkItemModel/Services/WorkItemModelService" />
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IVssPageService } from "VSS/Platform/Page";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { QueryErrorPolicy } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { DalFields } from "Wit/Common/Generated/Constants";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { IWorkItemNodeService } from "Wit/Common/WorkItemNodeService/IWorkItemNodeService";
import { IFieldLookup, IFieldProjectData, IWorkItemData, IWorkItemTypeData } from "Wit/WorkItemDataManager/Contracts";
import { WorkItemStoreLevelWITDataSource } from "Wit/WorkItemDataManager/Sources/WorkItemStoreLevelWITDataSource";
import { IWorkItemDataManager } from "Wit/WorkItemDataManager/WorkItemDataManager";
import { IRouteParams, IWorkItem, IWorkItemNodeData, IWorkItemPermissionData, IWorkItemTypeExtension } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import * as Resources from "Wit/WorkItemModel/Resources";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { WorkItem } from "Wit/WorkItemModel/WorkItem/WorkItem";
import { subtract } from "../WorkItem/FieldUtils";
import { Project } from "../WorkItem/Project";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags"
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

const TAGS_MRU_SETTINGS_KEY = "TagsMru";
const TAGS_MRU_TAGS_KEY = "Tags";
const TAGS_MRU_MAX_LENGTH = 5;

class WorkItemModelService extends VssService implements IWorkItemModelService {
    private workItemDataManager: IWorkItemDataManager;
    private workItemMap: { [workItemId: number]: IWorkItem };
    private workItemTypeExtensions: { [key: string]: IWorkItemTypeExtension } = {};

    // cache requests for individual work items
    private workItemPromiseCache = new Map<number, Promise<IWorkItem[]>>();

    private workItemTagsMruCache: string[];

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
        this.workItemDataManager = pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
        this.workItemMap = {};
    }

    public addWorkItemToMap(id: number, workItem: IWorkItem) {
        this.workItemMap[id] = workItem;
    }

    public clearWorkItems(): void {
        this.workItemPromiseCache.clear();
        this.workItemMap = {};
        this.workItemTypeExtensions = {};
    }

    public clearWorkItem(id: number): void {
        this.workItemPromiseCache.delete(id);
        delete this.workItemMap[id];
    }

    public async createWorkItem(typeData: IWorkItemTypeData, projectId: string, data?: Partial<IWorkItemData>): Promise<IWorkItem> {
        const fieldProjectData = await this.getFieldProjectData(projectId);

        let globalSets: { [key: string]: string[] } = {};
        if (typeData.rules.globals.length > 0) {
            globalSets = await this.getConstantSets(typeData.rules.globals);
        }

        const nodeService = this.pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
        const areaNodes = await nodeService.getAreaPathNodes(projectId);
        const iterationNodes = await nodeService.getIterationNodes(projectId);

        if (areaNodes === undefined || iterationNodes === undefined) {
            throw new Error("Unable to find area or iteration nodes for project: " + projectId);
        }

        let areaId = (await nodeService.getMruAreaPaths(projectId))[0];
        let iterationId = (await nodeService.getMruIterations(projectId))[0];

        if (this.checkPage()) {
            const teamSettingsService = this.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
            const teamSettings = await teamSettingsService.getTeamSettings()!;

            if (teamSettings?.referencedNodes?.areaNodes) {
                areaId = teamSettings?.referencedNodes?.areaNodes[0].id ?? areaId;
            }

            // In case of specific team context, iteration priority should be:
            // 1. Default iteration
            // 2. Current iteration
            // 3. MRU iteration
            if (teamSettings) {
                iterationId = teamSettings.defaultIteration?.nodeId ?? teamSettings.currentIteration?.nodeId ?? iterationId;
            }
        }

        if (!areaId) {
            // using root area if there's no MRU or other area data
            areaId = areaNodes.id;
        }

        const workItemNodeData: IWorkItemNodeData = {
            areaPathMru: areaId,
            areaPathNodes: areaNodes,
            iterationPathMru: iterationId,
            iterationPathNodes: iterationNodes
        };

        if (fieldProjectData) {
            return await this.createWorkItemModel(typeData, fieldProjectData, workItemNodeData, globalSets, data);
        } else {
            throw new Error(Resources.FailedToRetrieveProjectDataForId);
        }
    }

    private checkPage(): boolean {
        const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const pagePivot = navigationService.getRouteValues()["pivot"];
        //for add button on pages board, backlog and taskboard in sprints it should return true
        //to let change default area and iterations values for new work items
        return pagePivot === "backlog" || pagePivot === "board" || pagePivot === "taskboard";
    }

    // This method has different behavior if ids contains duplicate ids.
    // If this.workItemMap doesn't contain id, then the return result of this function
    // will be array without duplicated workItem object. But if this.workItemMap already
    // contains element, then in the result array this element will be duplicated.
    //
    // Example: argument ids = [1, 1, 2]
    // If this.workItemMap doesn't contains id = 1, then the return result will be
    // workItems = [IWorkItem with id = 1, IWorkItem with id = 2], otherwise
    // workItems = [IWorkItem with id = 1, IWorkItem with id = 1, IWorkItem with id = 2]
    public async getWorkItems(
        ids: number[],
        isDeleted?: boolean,
        includeRecentActivity?: boolean,
        includeHistory?: boolean,
        isCacheDisabled?: boolean,
        errorPolicy?: QueryErrorPolicy
    ): Promise<IWorkItem[]> {
        if (!ids || !ids.length) {
            return [];
        }
        const workItems: IWorkItem[] = [];
        const idsToFetch: number[] = [];

        ids.forEach(id => {
            const workItem = this.workItemMap[id];

            if (workItem && !isCacheDisabled) {
                workItems.push(workItem);
            } else if (!idsToFetch.includes(id)) {
                idsToFetch.push(id);
            }
        });

        if (idsToFetch.length) {
            const shouldCheckWorkItemsLength = errorPolicy === undefined || errorPolicy === QueryErrorPolicy.Fail;
            const workItemData = await this.workItemDataManager.beginGetWorkItemData(
                idsToFetch,
                isDeleted,
                includeRecentActivity,
                includeHistory,
                errorPolicy
            );
            if (!workItemData || (shouldCheckWorkItemsLength && workItemData.length !== idsToFetch.length)) {
                const missingIds = idsToFetch.filter(value => {
                    return !workItemData?.some(data => {
                        data.fields[DalFields.IDId] === value;
                    });
                });
                console.error(format(Resources.FailedToRetrieveWorkItemDataForIds, missingIds.join(", ")));
            } else {
                const cachedWorkItemTypeData = new Map<string, IWorkItemTypeData[] | null>();
                const cachedFieldProjectData = new Map<string, IFieldProjectData | null>();
                const cachedProjects = new Map<string, Project>();

                for (const data of workItemData) {
                    let workItemType;
                    const key = `${data.projectId}-${data.fields[DalFields.WorkItemTypeId]}`;
                    if (!cachedWorkItemTypeData.has(key)) {
                        workItemType = await this.workItemDataManager.beginGetWorkItemTypeData(data.projectId, [
                            data.fields[DalFields.WorkItemTypeId]
                        ]);
                        cachedWorkItemTypeData.set(key, workItemType);
                    } else {
                        workItemType = cachedWorkItemTypeData.get(key);
                    }

                    let fieldProjectData : IFieldProjectData | null | undefined;
                    if (!cachedFieldProjectData.has(data.projectId)) {
                        fieldProjectData = await this.workItemDataManager.beginGetFieldProjectData(data.projectId);
                        cachedFieldProjectData.set(data.projectId, fieldProjectData);
                    } else {
                        fieldProjectData = cachedFieldProjectData.get(data.projectId);
                    }

                    if (workItemType && fieldProjectData) {
                        let globalSets: { [key: string]: string[] } = {};
                        if (workItemType[0].rules.globals.length > 0) {
                            globalSets = await this.getConstantSets(workItemType[0].rules.globals);
                        }

                        const nodeService = this.pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
                        const areaNodes = await nodeService.getAreaPathNodes(data.projectId);
                        const iterationNodes = await nodeService.getIterationNodes(data.projectId);

                        if (areaNodes === undefined || iterationNodes === undefined) {
                            throw new Error("Unable to find area or iteration nodes for project: " + data.projectId);
                        }

                        let project: Project | undefined = undefined;
                        if (!cachedProjects.has(data.projectId)) {
                            project = new Project({
                                ...fieldProjectData!.projects[0],
                                areaNodes: areaNodes,
                                iterationNodes: iterationNodes
                            });
                            cachedProjects.set(data.projectId, project);
                        } else {
                            project = cachedProjects.get(data.projectId)!;
                        }

                        const workItem = await this.createWorkItemModel(
                            workItemType[0],
                            fieldProjectData,
                            { areaPathNodes: areaNodes, iterationPathNodes: iterationNodes },
                            globalSets,
                            data,
                            project
                        );

                        workItems.push(workItem);
                    }
                }
            }
        }

        return workItems;
    }

    public async refreshWorkItemLinks(workItem: IWorkItem): Promise<void> {
        const links = workItem.getLinks().filter(x => {
            return localeIgnoreCaseComparer(x.baseLinkType, "WorkItemLink") === 0;
        });

        for (const link of links) {
            if (!link.linkData.ID) {
                continue;
            }

            this.clearWorkItem(link.linkData.ID);
            const linkWorkItem = await this.getWorkItem(link.linkData.ID);

            linkWorkItem.refresh().catch(reason => alert(reason));
        }
    }

    public async getWorkItem(
        id: number,
        config?: { isDeleted?: boolean; includeRecentActivity?: boolean; includeHistory?: boolean }
    ): Promise<IWorkItem> {
        let workItemsPromise = this.workItemPromiseCache.get(id);
        if (!workItemsPromise) {
            workItemsPromise = this.getWorkItems([id], config?.isDeleted, config?.includeRecentActivity, config?.includeHistory);
            this.workItemPromiseCache.set(id, workItemsPromise);
        }

        const workItems = await workItemsPromise;
        if (!workItems.length) {
            throw Error(format(Resources.InvalidWorkItemId, id));
        }

        return workItems[0];
    }

    public getWorkItemTypeData(projectId: string, workItemTypeNames: string[]): Promise<IWorkItemTypeData[] | null> {
        return this.workItemDataManager.beginGetWorkItemTypeData(projectId, workItemTypeNames);
    }

    public async getConstantSets(ids: string[]): Promise<{ [key: string]: string[] }> {
        const result = await this.workItemDataManager.beginGetConstantSets(ids);
        if (result !== null) {
            return result;
        } else {
            return {};
        }
    }

    public getFieldProjectData(projectId: string): Promise<IFieldProjectData | null> {
        return this.workItemDataManager.beginGetFieldProjectData(projectId);
    }

    public async getFieldLookup(projectId: string): Promise<IFieldLookup | null> {
        const data = await this.getFieldProjectData(projectId);
        const fieldLookup: IFieldLookup = {};
        data &&
            data.fields.forEach(f => {
                fieldLookup[f.id] = f;
                fieldLookup[f.referenceName] = f;
            });

        return fieldLookup;
    }

    public async addTagsMru(newTags: string[]) {
        const mru = await this.getTagsMRU();
        const filteredMru = subtract(mru, newTags);
        const newMru = newTags.concat(filteredMru);
        newMru.splice(TAGS_MRU_MAX_LENGTH);
        this.workItemTagsMruCache = newMru;

        const key = `${TAGS_MRU_SETTINGS_KEY}/${TAGS_MRU_TAGS_KEY}`;
        const newEntries: { [key: string]: string[] } = {};
        newEntries[key] = newMru;
        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");

        return settingsService.setEntries(newEntries, SettingsUserScope.Me);
    }

    public async getTagsMRU(): Promise<string[]> {
        if (this.workItemTagsMruCache) {
            this.workItemTagsMruCache.slice();
        }

        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");

        const entries = await settingsService.getEntriesAsync(TAGS_MRU_SETTINGS_KEY, SettingsUserScope.Me);

        const mru: string[] = (entries && entries[TAGS_MRU_TAGS_KEY]) || [];
        // subtract out invalid values from cache
        this.workItemTagsMruCache = subtract(mru, [";;;;;", ";SUGGESTIONS;"]);
        // return a copy so modifying it won't change the local cache
        return this.workItemTagsMruCache.slice();
    }

    private async createWorkItemModel(
        typeData: IWorkItemTypeData,
        fieldProjectData: IFieldProjectData,
        workItemNodeData: IWorkItemNodeData,
        constantSets: { [key: string]: string[] },
        data?: Partial<IWorkItemData>,
        project?: Project
    ): Promise<IWorkItem> {
        data = data || ({} as IWorkItemData);

        if (!data.isCommentingAvailable) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const requestParams = {
                areaId: workItemNodeData.areaPathMru
            };
            const permissions = await contributionService.getDataAsync<IWorkItemPermissionData>(
                "ms.vss-work-web.work-item-permissions-data-provider",
                requestParams
            );

            const saveWorkItemCommentPermission = !!permissions?.saveWorkItemCommentPermission?.hasPermission;

            data.isCommentingAvailable = saveWorkItemCommentPermission;
        }

        const workItem = new WorkItem(this.pageContext, typeData, fieldProjectData, workItemNodeData, constantSets, data, undefined, project);
        this.workItemMap[workItem.id] = workItem;
        return workItem;
    }

    public getWorkItemTypeExtensions(extensionIds: string[]) {
        return new Promise<IWorkItemTypeExtension[]>((resolve, reject) => this.beginGetWorkItemTypeExtensions(extensionIds, resolve, reject));
    }

    public beginGetWorkItemTypeExtensions(
        extensionIds: string[],
        callback: (extensions: IWorkItemTypeExtension[]) => void,
        errorCallback?: (error: any) => void
    ) {
        const queue: string[][] = [];
        let extensionsToDownload: string[] | null = null;
        let queryStringLength = 0;
        let waitingExtensions = 0;
        let fetchedExtensions: IWorkItemTypeExtension[] = [];

        const completeAction = () => {
            if (waitingExtensions === 0) {
                callback.call(this, fetchedExtensions);
            }
        };

        const fetchExtensions = (extensionIds: string[]) => {
            const routeParams: IRouteParams = { controller: "wit", action: "workItemTypeExtensions" };
            const pageService = this.pageContext.getService<IVssPageService>("IVssPageService");
            const pageData = pageService.getData();
            new WorkItemStoreLevelWITDataSource(this.pageContext)
                .getDataFromCacheAsync<IWorkItemTypeExtension[]>(
                    routeParams,
                    { extensionIds: extensionIds },
                    pageData!.hostId,
                    routeParams.action,
                    "_"
                )
                .then(witDataSourceResponse => {
                    const extensions: IWorkItemTypeExtension[] = witDataSourceResponse.data;
                    // adding extensions to the store field definition, by doing this we can avoid calling entire getFields in Kanban board
                    extensions.forEach(extension => {
                        const fields = extension.fields.map(extensionField => {
                            return extensionField.field;
                        });
                        // this._prepareFields(fields, true); Not ported from old platform
                    });

                    let globals: string[] = [];
                    extensions.forEach(extension => {
                        this.workItemTypeExtensions[extension.id] = extension;
                        if (extension.globals) {
                            globals = globals.concat(extension.globals);
                        }
                    });

                    // hack to avoid loading constants which may not be necessary
                    if ($DEBUG && globals.length > 0) {
                        console.warn("Ignoring globals %O", globals);
                    }
                    fetchedExtensions = fetchedExtensions.concat(extensions);
                    waitingExtensions--;
                    completeAction();

                    // also not ported from old platform
                    // const workItemDataManager = this.pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
                    // this.beginEnsureConstantSets( // old way
                    //     globals,
                    //     () => {
                    //         fetchedExtensions = fetchedExtensions.concat(extensions);
                    //         waitingExtensions--;
                    //         completeAction();
                    //     },
                    //     errorCallback
                    // );
                }, errorCallback);
        };

        if (extensionIds && extensionIds.length > 0) {
            extensionIds.forEach(extensionId => {
                if (!this.workItemTypeExtensions[extensionId]) {
                    const extensionIdsLength = extensionId.length + 15; // &extensionIds=
                    // Handling code if the querystring in the url exceeds 1024 characters
                    if (queryStringLength + extensionIdsLength > 1024) {
                        queryStringLength = 0;
                        extensionsToDownload = null;
                    }

                    // In case of first batch or starting a new batch, pushing the extension Ids to the queue
                    if (!extensionsToDownload) {
                        extensionsToDownload = [extensionId];
                        queue.push(extensionsToDownload);
                        waitingExtensions++;
                    } else {
                        // Appends the extensionIds to the existing queue
                        extensionsToDownload.push(extensionId);
                    }
                    queryStringLength += extensionIdsLength;
                } else {
                    // Store the cached extensions to return to the callback
                    fetchedExtensions.push(this.workItemTypeExtensions[extensionId]);
                }
            });

            // If the queue is empty then everything is cached, we can return directly from the cache
            if (queue.length === 0) {
                completeAction();
            } else {
                while (queue.length > 0) {
                    fetchExtensions(queue.pop()!);
                }
            }
        } else {
            callback.call(this, fetchedExtensions);
        }
    }

    public isWorkItemCached(id: number): boolean {
        return !!this.workItemMap[id];
    }
}

const serviceName = "IWorkItemModelService";
Services.add(serviceName, { serviceFactory: WorkItemModelService });
